import { Link, Navigate } from 'react-router-dom';
import { linkButton } from '../components/panels/today';
import { Block, ErrorNote, Loading, Row, Screen } from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import type { ProgramSummary } from '../lib/api';
import { useFeature, useFeatureLabel, usePrograms } from '../lib/queries';

const metaFor = (p: ProgramSummary, plan: string) =>
  [
    `${p.daysCount} ${p.daysCount === 1 ? 'day' : 'days'}`,
    p.locked ? plan : null,
    p.enrollment?.status === 'completed'
      ? 'Completed'
      : p.enrollment?.status === 'left'
        ? 'Left'
        : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** The guided programs on offer, with the one the member is in first. */
export function ProgramsPage() {
  const state = useFeature('programs');
  const plan = useFeatureLabel('programs');
  const programs = usePrograms(state !== 'off');

  if (state === 'off') return <Navigate to="/achieve" replace />;
  if (state === 'locked') {
    return (
      <Screen back="/achieve" eyebrow="Programs" title="Guided programs">
        <ProLock feature="programs" />
      </Screen>
    );
  }
  if (programs.isPending) return <Loading />;
  if (programs.isError)
    return <ErrorNote error={programs.error} retry={() => void programs.refetch()} />;

  const active = programs.data.find((p) => p.enrollment?.status === 'active') ?? null;
  const rest = programs.data.filter((p) => p !== active);
  return (
    <Screen
      back="/achieve"
      eyebrow="Programs"
      title="Guided programs"
      subtitle="A short read, one question for Master Kala and one thing to do, every day. One program at a time."
    >
      {active?.enrollment && (
        <Block
          eyebrow={`Day ${active.enrollment.currentDay} of ${active.daysCount}`}
          title={active.title}
          action={
            <Link
              to={`/programs/${active.id}/days/${active.enrollment.currentDay}`}
              className={linkButton}
            >
              Open today
            </Link>
          }
        >
          {active.enrollment.completedDays} of {active.daysCount} days done.{' '}
          <Link to={`/programs/${active.id}`} className="underline underline-offset-4">
            All days
          </Link>
        </Block>
      )}
      {rest.map((p) => (
        <Row key={p.id} title={p.title} meta={metaFor(p, plan)} to={`/programs/${p.id}`} />
      ))}
      {programs.data.length === 0 && (
        <p className="px-5 py-6 text-sm text-muted-foreground">No programs yet.</p>
      )}
    </Screen>
  );
}
